import { Link } from "react-router-dom";
import { ArrowLeft, Radar, LifeBuoy, Phone, Globe } from "lucide-react";

const US_RESOURCES = [
  { name: "National Problem Gambling Helpline", contact: "1-800-GAMBLER (1-800-426-2537)", note: "Call or text · 24/7 · confidential" },
  { name: "NCPGambling.org", contact: "ncpgambling.org/chat", note: "Live chat + resources from the National Council on Problem Gambling" },
  { name: "Gamblers Anonymous", contact: "gamblersanonymous.org", note: "Peer support meetings across the U.S." },
];

const CA_RESOURCES = [
  { name: "ConnexOntario (Ontario)", contact: "1-866-531-2600", note: "Free, confidential help · 24/7" },
  { name: "Responsible Gambling Council", contact: "responsiblegambling.org", note: "Canada-wide education & support" },
  { name: "Government of Canada — Problem Gambling", contact: "canada.ca", note: "Provincial helpline directory" },
];

function Group({ title, icon, items }) {
  return (
    <section className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
      <h2 className="font-heading text-lg font-bold text-slate-100 mb-3 flex items-center gap-2">{icon} {title}</h2>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.name} className="border-l-2 border-[#00E5A3]/40 pl-3">
            <div className="text-sm font-semibold text-slate-100">{r.name}</div>
            <div className="text-sm text-[#00E5A3] font-mono">{r.contact}</div>
            <div className="text-xs text-slate-500">{r.note}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ResponsibleGaming() {
  return (
    <div className="min-h-screen bg-[#080B11] text-slate-300">
      <header className="border-b border-[#232D42] px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#00E5A3] flex items-center justify-center"><Radar size={18} className="text-[#04140F]" /></div>
          <span className="font-heading font-extrabold text-lg text-slate-100">Sideline<span className="text-[#00E5A3]">IQ</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14} /> Home</Link>
      </header>
      <div data-testid="responsible-gaming-page" className="max-w-[820px] mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-3xl font-extrabold text-slate-50 mb-2 flex items-center gap-2">
          <LifeBuoy size={26} className="text-[#00E5A3]" /> Responsible Gaming
        </h1>
        <p className="text-sm leading-relaxed text-slate-400 mb-8 max-w-2xl">
          SidelineIQ is an <b className="text-slate-200">information and research tool</b> — we don't take bets or offer picks.
          If you choose to wager elsewhere, please do so responsibly. Set limits, never chase losses, and only play with money you can
          afford to lose. If gambling stops being fun, help is free, confidential, and available 24/7.
        </p>

        <div className="space-y-4">
          <Group title="United States" icon={<Phone size={18} className="text-[#00E5A3]" />} items={US_RESOURCES} />
          <Group title="Canada" icon={<Globe size={18} className="text-[#00E5A3]" />} items={CA_RESOURCES} />
        </div>

        <section className="mt-6 rounded-xl border border-[#F5A524]/30 bg-[#F5A524]/5 p-5">
          <h2 className="font-heading text-lg font-bold text-slate-100 mb-2">Signs it may be a problem</h2>
          <ul className="text-sm text-slate-400 space-y-1 list-disc pl-5">
            <li>Betting more than you can afford, or chasing losses</li>
            <li>Gambling to escape stress or feel better</li>
            <li>Lying to family or friends about your gambling</li>
            <li>Feeling anxious, restless or irritable when not gambling</li>
          </ul>
          <p className="text-sm text-slate-400 mt-3">You must be <b className="text-slate-200">18+ (21+ where required)</b> to bet. Reach out early — support works.</p>
        </section>

        <p className="text-[11px] text-slate-600 mt-8 border-t border-[#232D42] pt-6">
          SidelineIQ is not affiliated with, endorsed by, or licensed by any sportsbook, casino, or professional sports league.
          Resource listings are provided for convenience and do not constitute an endorsement.
        </p>
      </div>
    </div>
  );
}
